import constate from 'constate';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceEventEmitter, EmitterSubscription } from 'react-native';

import { Colors as DefaultColors } from '../colors';
import LoadingView from '../components/loading-view';
import Config from '../config.json';
import { Fonts as DefaultFonts } from '../fonts';
import i18n, { initTranslationWithXHREndpoint } from '../i18n';
import type { ColorsType } from '../interfaces/colors.type';
import type { FontsType } from '../interfaces/fonts.type';
import { Languages } from '../interfaces/languages';
import type { LessonEvent } from '../interfaces/lesson-event';
import type {
  ENVIRONMENT,
  LessonPlayerConfig,
} from '../interfaces/lesson-player-config';
import type { StudentInfo } from '../interfaces/student-info';

interface LessonPlayerProps {
  colors?: ColorsType;
  fonts?: FontsType;
  icon?: any;
  config: LessonPlayerConfig;
  children?: React.ReactNode;
}

interface LessonPlayerResult {
  colors: ColorsType;
  fonts: FontsType;
  context: {
    indexVisible: boolean;
    toggleIndexVisibility: () => void;
    translationActive: boolean;
    toggleTranslation: () => void;
    hideIndex: () => void;
    logEvent: (event: LessonEvent) => void;
    listenToEvent(callback: (event: any) => void): EmitterSubscription;
    isTranslationButtonEnabled: boolean;
    showTranslationButton: boolean;
    environment: ENVIRONMENT;
    centrosModeEnabled: boolean;
    referralMessageElligible: boolean;
    referralBonusAmount?: string;
  };
  studentInfo?: StudentInfo;
  indicator: [number, (position: number) => void];
  soundPreferences: {
    areSoundEffectsEnabled: boolean;
  };
}

function LessonPlayer({
  colors = undefined,
  fonts = undefined,
  config: {
    isTranslationButtonEnabled = false,
    showTranslationButton = false,
    areSoundEffectsEnabled = true,
    preferredLang = Languages.EN,
    environment,
    studentInfo = undefined,
    centrosModeEnabled = false,
    referralMessageElligible = false,
    referralBonusAmount,
  },
}: LessonPlayerProps): LessonPlayerResult {
  const [indexVisible, setIndexVisible] = useState(false);
  const [translationActive, setTranslationActive] = useState(false);
  const indicator = useState(0);
  const { ready: i18nReady } = useTranslation();

  useEffect(() => {
    if (!i18nReady) {
      return;
    }

    i18n.changeLanguage(preferredLang ?? Languages.EN);
  }, [preferredLang, i18nReady]);

  useEffect(() => {
    if (environment) {
      const endpoint = Config[environment].thirdpartyService;
      initTranslationWithXHREndpoint(endpoint, preferredLang);
    }
  }, [environment, preferredLang]);

  const toggleIndexVisibility = () => {
    setIndexVisible((visible) => !visible);
  };

  const toggleTranslation = () => {
    setTranslationActive((translation) => !translation);
  };

  const hideIndex = useCallback(() => {
    setIndexVisible(false);
  }, []);

  const logEvent = useCallback((event: LessonEvent) => {
    DeviceEventEmitter.emit('lessonEvent', event);
  }, []);

  const listenToEvent = (callback): EmitterSubscription => {
    return DeviceEventEmitter.addListener('lessonEvent', callback);
  };

  return {
    context: {
      indexVisible,
      toggleIndexVisibility,
      translationActive,
      toggleTranslation,
      hideIndex,
      logEvent,
      listenToEvent,
      isTranslationButtonEnabled: !!isTranslationButtonEnabled,
      showTranslationButton: !!showTranslationButton,
      environment,
      centrosModeEnabled,
      referralMessageElligible,
      referralBonusAmount,
    },
    studentInfo,
    indicator,
    colors: { ...DefaultColors, ...colors },
    fonts: {
      primary: { ...DefaultFonts.primary, ...fonts?.primary },
      secondary: { ...DefaultFonts.secondary, ...fonts?.secondary },
    },
    soundPreferences: {
      areSoundEffectsEnabled: !!areSoundEffectsEnabled,
    },
  };
}

const [
  LessonPlayerProviderCtx,
  useLessonContext,
  useColors,
  useFont,
  useSoundPreferences,
  usePositionIndicator,
  useStudentInfo,
] = constate(
  LessonPlayer,
  (value) => value.context,
  (value) => value.colors,
  (value) => value.fonts,
  (value) => value.soundPreferences,
  (value) => value.indicator,
  (value) => value.studentInfo
);

const LessonPlayerProvider: React.FC<LessonPlayerProps> = (props) => {
  return (
    <Suspense fallback={<LoadingView />}>
      <LessonPlayerProviderCtx {...props} />
    </Suspense>
  );
};

export {
  LessonPlayerProvider,
  useLessonContext,
  useColors,
  useFont,
  useSoundPreferences,
  usePositionIndicator,
  useStudentInfo,
};
